import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Input, Button } from "reactstrap";
import {
  createCarMake, updateCarMake,
  createCarModel, updateCarModel,
  createCarColor, updateCarColor,
  approveCarSuggestion,
} from "../../utils/vehicleApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";

const headerStyle = { borderBottom: `2px solid ${C.borderLight}` };
const bodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const footerStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
const inputStyle = { borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, padding: "8px 12px", color: C.text };
const coralBtnStyle = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const ghostBtnStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };

const TITLES = { make: "Make", model: "Model", color: "Colour" };

// One modal for all three lookup types, plus the approve-a-suggestion flow.
//
// `kind`      : "make" | "model" | "color"
// `row`       : existing record, or null to create
// `approving` : when true, saving promotes a pending suggestion instead of doing a
//               plain update. Approving opens this pre-filled precisely so the admin
//               fixes casing and supplies the Arabic name BEFORE it goes global —
//               a raw customer-typed string would ship untranslated to the app.
const LookupModal = ({ isOpen, toggle, onSaved, kind, row, makes = [], approving = false }) => {
  const editing = !!row;
  const [name, setName] = useState("");
  const [arabicName, setArabicName] = useState("");
  const [carMakeId, setCarMakeId] = useState("");
  const [hex, setHex] = useState("#FFFFFF");
  const [sortOrder, setSortOrder] = useState("0");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(row?.name || "");
    setArabicName(row?.arabicName || "");
    setCarMakeId(row?.carMakeId ? String(row.carMakeId) : "");
    setHex(row?.hex || "#FFFFFF");
    setSortOrder(row?.sortOrder != null ? String(row.sortOrder) : "0");
    setIsActive(row?.isActive ?? true);
  }, [isOpen, row]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) return errorToast("Name is required");
    // Arabic is required for makes and colours because those strings render in the
    // customer app. Models fall back to the Latin name, which is how Saudi customers
    // read them anyway ("Camry", "Land Cruiser").
    if (kind !== "model" && !arabicName.trim()) return errorToast("Arabic name is required");
    if (kind === "model" && !carMakeId) return errorToast("Select a make");
    if (kind === "color" && hex && !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      return errorToast("Colour must be a hex value like #FFFFFF");
    }

    const payload = {
      name: name.trim(),
      arabicName: arabicName.trim() || null,
      sortOrder: Number(sortOrder) || 0,
      isActive,
      ...(kind === "model" && { carMakeId: Number(carMakeId) }),
      ...(kind === "color" && { hex: hex || null }),
    };

    setSaving(true);
    let result;
    if (approving) {
      result = await approveCarSuggestion(row.id, {
        type: kind === "model" ? "model" : "make",
        name: payload.name,
        arabicName: payload.arabicName,
        sortOrder: payload.sortOrder,
      });
    } else if (kind === "make") {
      result = editing ? await updateCarMake(row.id, payload) : await createCarMake(payload);
    } else if (kind === "model") {
      result = editing ? await updateCarModel(row.id, payload) : await createCarModel(payload);
    } else {
      result = editing ? await updateCarColor(row.id, payload) : await createCarColor(payload);
    }
    setSaving(false);

    if (result) {
      successToast(
        approving
          ? `${payload.name} approved`
          : `${TITLES[kind]} ${editing ? "updated" : "created"}`
      );
      toggle();
      onSaved?.();
    }
  };

  const heading = approving
    ? `Approve "${row?.name}"`
    : `${editing ? "Edit" : "Add"} ${TITLES[kind]}`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle} style={headerStyle}>{heading}</ModalHeader>
        <ModalBody style={bodyStyle}>
          {approving && (
            <div style={{
              background: C.orangeLight, border: `1px solid ${C.orange}33`, borderRadius: 8,
              padding: "10px 12px", fontSize: 12, color: C.textMed, marginBottom: 16,
            }}>
              Suggested by a customer. Correct the spelling and add the Arabic name before
              approving — once approved it is visible to every customer.
            </div>
          )}

          {kind === "model" && (
            <FormGroup>
              <label style={labelStyle}>Make</label>
              <Input type="select" style={inputStyle} value={carMakeId}
                onChange={(e) => setCarMakeId(e.target.value)}>
                <option value="">Select a make…</option>
                {makes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Input>
            </FormGroup>
          )}

          <FormGroup>
            <label style={labelStyle}>Name (English)</label>
            <Input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)}
              placeholder={kind === "color" ? "e.g. White" : "e.g. Toyota"} />
          </FormGroup>

          <FormGroup>
            <label style={labelStyle}>
              Name (Arabic){kind === "model" && <span style={{ color: C.textLight, fontWeight: 400 }}> — optional</span>}
            </label>
            <Input style={{ ...inputStyle, direction: "rtl" }} value={arabicName}
              onChange={(e) => setArabicName(e.target.value)}
              placeholder={kind === "model" ? "يُعرض بالإنجليزية إن ترك فارغًا" : "أدخل الاسم بالعربية"} />
          </FormGroup>

          {kind === "color" && (
            <FormGroup>
              <label style={labelStyle}>Swatch</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={hex} onChange={(e) => setHex(e.target.value.toUpperCase())}
                  style={{ width: 44, height: 38, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: 2, cursor: "pointer", background: C.white }} />
                <Input style={{ ...inputStyle, maxWidth: 140 }} value={hex}
                  onChange={(e) => setHex(e.target.value.toUpperCase())} placeholder="#FFFFFF" />
              </div>
            </FormGroup>
          )}

          <FormGroup>
            <label style={labelStyle}>Sort order</label>
            <Input type="number" style={{ ...inputStyle, maxWidth: 140 }} value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)} />
          </FormGroup>

          {!approving && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text, fontWeight: 600 }}>
              <Input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                style={{ position: "static", margin: 0 }} />
              Active
            </label>
          )}
        </ModalBody>
        <ModalFooter style={footerStyle}>
          <Button type="button" onClick={toggle} style={ghostBtnStyle}>Cancel</Button>
          <Button type="submit" disabled={saving} style={coralBtnStyle}>
            {saving ? "Saving…" : approving ? "Approve" : "Save"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default LookupModal;
